import { Box, Button, Stack } from "@mui/material";
import AddPoolCard from "features/wallet/components/AddPoolCard";
import TransferCard from "features/wallet/components/TransferCard";
import ExchangeCard from "features/wallet/components/ExchangeCard";
import EditLiquidityPoolModal from "features/wallet/components/EditLiquidityPoolModal";
import PoolTransactionHistory from "features/wallet/components/PoolTransactionHistory";
import WalletTotalCard from "features/wallet/pages/wallet-content/WalletTotalCard";
import WalletTotalCardSkeleton from "features/wallet/pages/wallet-content/WalletTotalCardSkeleton";
import WalletPoolsPanelList from "features/wallet/panel/WalletPoolsPanelList";
import { AccountsRefreshingState } from "features/wallet/pages/wallet-content/AccountsLoadingStates";
import { useWalletScreen } from "features/wallet/hooks/useWalletScreen";
import { useTranslation } from "shared/hooks/useTranslation";
import { toast } from "sonner";

export default function WalletPanelPane() {
    const { t, getPathWithLanguage } = useTranslation();
    const screen = useWalletScreen();
    const {
        locale,
        username,
        token,
        userSettings,
        balancesVisible,
        setBalancesVisible,
        pools,
        loading,
        error,
        createPool,
        updatePool,
        transfer,
        exchange,
        offline: { mutationsDisabled, disabledIfOffline, offlineTitle, isOnline },
        totals: { sections, estimatedTotal },
        dialogs: {
            editModalOpen,
            setEditModalOpen,
            poolForEdit,
            setPoolForEdit,
            historyPool,
            setHistoryPool,
            addPoolOpen,
            setAddPoolOpen,
            transferOpen,
            setTransferOpen,
            exchangeOpen,
            setExchangeOpen,
        },
        poolMenu: {
            poolMenuAnchor,
            handlePoolMenuOpen,
            handlePoolMenuClose,
            handleEditClick,
            handleDeleteClick,
        },
        actions: { handlePoolClick, handleAddPoolShortcut, subtitleForPool },
        initialLoading,
    } = screen;

    const accountsPath = username ? getPathWithLanguage(`${username}/accounts`) : "#";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                gap: 1.5,
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                {initialLoading ? (
                    <WalletTotalCardSkeleton
                        variant="compact"
                        title={t("wallet.estimatedTotal")}
                        label={t("wallet.loadingAccounts")}
                    />
                ) : (
                    <WalletTotalCard
                        variant="compact"
                        estimatedTotal={estimatedTotal}
                        displayCurrency={userSettings.displayCurrency}
                        sections={sections}
                        balancesVisible={balancesVisible}
                        locale={locale}
                        onToggleBalances={() => setBalancesVisible(!balancesVisible)}
                        infoLabel={t("wallet.estimatedTotalHelp")}
                        hideBalancesLabel={t("common.hideBalances")}
                        showBalancesLabel={t("common.showBalances")}
                        title={t("wallet.estimatedTotal")}
                    />
                )}
            </Box>

            {!initialLoading && loading && sections.length > 0 && (
                <AccountsRefreshingState label={t("wallet.loadingAccounts")} />
            )}

            <WalletPoolsPanelList
                sections={sections}
                poolCount={pools.length}
                loading={loading}
                error={error}
                locale={locale}
                balancesVisible={balancesVisible}
                mutationsDisabled={mutationsDisabled}
                isOnline={isOnline}
                offlineTitle={offlineTitle}
                accountsPath={accountsPath}
                poolMenuAnchor={poolMenuAnchor}
                subtitleForPool={subtitleForPool}
                onAddAccount={handleAddPoolShortcut}
                onPoolClick={handlePoolClick}
                onPoolMenuOpen={handlePoolMenuOpen}
                onPoolMenuClose={handlePoolMenuClose}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
            />

            <Box
                sx={{
                    flexShrink: 0,
                    pt: 0.5,
                    borderTop: 1,
                    borderColor: "divider",
                }}
            >
                <Stack direction="column" spacing={1}>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => setTransferOpen(true)}
                        disabled={disabledIfOffline(pools.length < 2)}
                        title={!isOnline && pools.length >= 2 ? offlineTitle : undefined}
                        sx={{ minHeight: 40, borderRadius: 999, fontWeight: 800, textTransform: "none" }}
                    >
                        {t("wallet.transfer")}
                    </Button>
                    <Button
                        fullWidth
                        variant="outlined"
                        color="secondary"
                        size="small"
                        onClick={() => setExchangeOpen(true)}
                        disabled={disabledIfOffline(pools.length < 2)}
                        title={!isOnline && pools.length >= 2 ? offlineTitle : undefined}
                        sx={{ minHeight: 40, borderRadius: 999, fontWeight: 800, textTransform: "none" }}
                    >
                        {t("wallet.exchange")}
                    </Button>
                </Stack>
            </Box>

            <AddPoolCard
                open={addPoolOpen}
                onClose={() => setAddPoolOpen(false)}
                variant="dialog"
                mutationsDisabled={mutationsDisabled}
                onSubmit={async (data) => {
                    const result = await createPool(data);
                    if (result.success) toast.success(t("wallet.poolCreated"));
                    return result;
                }}
            />

            <EditLiquidityPoolModal
                open={editModalOpen}
                pool={poolForEdit}
                mutationsDisabled={mutationsDisabled}
                onClose={() => {
                    setEditModalOpen(false);
                    setPoolForEdit(null);
                }}
                onSubmit={async (poolId, data) => {
                    const result = await updatePool(poolId, data);
                    if (result.success) toast.success(t("wallet.poolUpdated"));
                    return result;
                }}
            />

            <TransferCard
                pools={pools}
                open={transferOpen}
                onClose={() => setTransferOpen(false)}
                variant="dialog"
                mutationsDisabled={mutationsDisabled}
                onSubmit={async (data) => {
                    const result = await transfer(data);
                    if (result.success) toast.success(t("wallet.transferSuccess"));
                    return result;
                }}
            />

            <ExchangeCard
                pools={pools}
                open={exchangeOpen}
                onClose={() => setExchangeOpen(false)}
                variant="dialog"
                mutationsDisabled={mutationsDisabled}
                onSubmit={async (data) => {
                    const result = await exchange(data);
                    if (result.success) toast.success(t("wallet.exchangeSuccess"));
                    return result;
                }}
            />

            <PoolTransactionHistory
                open={Boolean(historyPool)}
                poolId={historyPool?._id ?? ""}
                poolName={historyPool?.name ?? ""}
                username={username ?? ""}
                token={token}
                onClose={() => setHistoryPool(null)}
            />
        </Box>
    );
}
